import { useCallback, useEffect, useRef, useState } from 'react';
import { BASE_URL } from '../../../utils/config';
import { useAppSelector } from '../../../store/hooks';
import { getAuthToken } from '../../../store/selectors/auth';
import type { RootState } from '../../../store/types';
import { assistantVoiceCapture } from '../../../services/AssistantVoiceCapture';

const LS_KEY = 'i2x-coach:assistantChatState';

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    timingMs?: number;
    sources?: string[];
}

export type ChatErrorKind = 'init' | 'request' | 'voice';

export interface ChatError {
    kind: ChatErrorKind;
    message: string;
}

export type VoiceStatus = 'idle' | 'recording' | 'transcribing';

export interface LocalizationLike {
    getText: (key: string, options?: Record<string, string | number | boolean | null | undefined>) => string;
}

interface PersistedAssistantChatStateV1 {
    v: 1;
    projectId: string;
    history: ChatMessage[];
}

interface AssistantChatResponse {
    answer?: string;
    error?: string;
    timing?: {
        totalMs?: number;
    };
    sources?: { title?: string }[];
}

interface TranscriptionResponse {
    text?: string;
    error?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toChatMessage(value: unknown): ChatMessage | null {
    if (!isRecord(value)) return null;
    if (value.role !== 'user' && value.role !== 'assistant') return null;
    if (typeof value.content !== 'string' || value.content.length === 0) return null;

    const message: ChatMessage = { role: value.role, content: value.content };
    if (typeof value.timingMs === 'number') message.timingMs = value.timingMs;
    if (Array.isArray(value.sources)) {
        message.sources = value.sources.filter((source): source is string => typeof source === 'string' && source.length > 0);
    }
    return message;
}

function readPersistedState(projectId: string): ChatMessage[] {
    try {
        const raw = window.localStorage.getItem(LS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as PersistedAssistantChatStateV1;
        if (!isRecord(parsed) || parsed.v !== 1 || parsed.projectId !== projectId || !Array.isArray(parsed.history)) {
            return [];
        }
        return parsed.history.map(toChatMessage).filter((message): message is ChatMessage => message !== null);
    } catch {
        return [];
    }
}

function persistState(projectId: string, history: ChatMessage[]) {
    try {
        const payload: PersistedAssistantChatStateV1 = { v: 1, projectId, history };
        window.localStorage.setItem(LS_KEY, JSON.stringify(payload));
    } catch {
        // ignore storage failures
    }
}

function errorMessage(error: unknown, fallback: string) {
    return error instanceof Error && error.message ? error.message : fallback;
}

export function useAssistantChat({ localization, projectId }: { localization: LocalizationLike; projectId: string }) {
    const authToken = useAppSelector((state: RootState) => getAuthToken(state) || '');
    const [history, setHistory] = useState<ChatMessage[]>(() => readPersistedState(projectId));
    const [input, setInput] = useState('');
    const [isInitializing, setIsInitializing] = useState(true);
    const [isReady, setIsReady] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<ChatError | null>(null);
    const [initAttempt, setInitAttempt] = useState(0);
    const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>('idle');
    const [recordingStartedAt, setRecordingStartedAt] = useState<number | null>(null);

    const historyRef = useRef<ChatMessage[]>(history);
    const initRequestIdRef = useRef(0);
    const voiceCaptureActiveRef = useRef(false);
    const voiceRequestIdRef = useRef(0);

    const authHeaders = useCallback(
        (contentType: string): Record<string, string> => ({
            'Content-Type': contentType,
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        }),
        [authToken]
    );

    const updateHistory = useCallback((next: ChatMessage[]) => {
        historyRef.current = next;
        setHistory(next);
    }, []);

    useEffect(() => {
        historyRef.current = history;
        persistState(projectId, history);
    }, [history, projectId]);

    const resetVoice = useCallback(() => {
        voiceRequestIdRef.current += 1;
        if (voiceCaptureActiveRef.current) {
            assistantVoiceCapture.cancel();
            voiceCaptureActiveRef.current = false;
        }
        setVoiceStatus('idle');
        setRecordingStartedAt(null);
    }, []);

    useEffect(() => {
        updateHistory(readPersistedState(projectId));
        setInput('');
        setError(null);
        resetVoice();
    }, [projectId, resetVoice, updateHistory]);

    useEffect(() => {
        return () => {
            if (voiceCaptureActiveRef.current) {
                assistantVoiceCapture.cancel();
                voiceCaptureActiveRef.current = false;
            }
        };
    }, []);

    useEffect(() => {
        const requestId = ++initRequestIdRef.current;

        async function initAssistant() {
            setIsInitializing(true);
            setIsReady(false);
            setError(current => (current?.kind === 'init' ? null : current));

            try {
                const res = await fetch(`${BASE_URL}/ai/v1/assistant/${projectId}/init`, {
                    method: 'POST',
                    headers: authHeaders('application/json'),
                    body: JSON.stringify({})
                });

                if (initRequestIdRef.current !== requestId) return;

                if (!res.ok) {
                    const data = (await res.json().catch(() => ({}))) as { error?: string };
                    throw new Error(data.error || localization.getText('assistant_chat.init_failed'));
                }

                setIsReady(true);
            } catch (initError: unknown) {
                if (initRequestIdRef.current !== requestId) return;
                setError({ kind: 'init', message: errorMessage(initError, localization.getText('assistant_chat.init_failed')) });
            } finally {
                if (initRequestIdRef.current === requestId) {
                    setIsInitializing(false);
                }
            }
        }

        void initAssistant();
    }, [authHeaders, localization, projectId, initAttempt]);

    const retryInit = useCallback(() => setInitAttempt(attempt => attempt + 1), []);
    const dismissError = useCallback(() => setError(null), []);

    const clearConversation = useCallback(() => {
        updateHistory([]);
        setError(current => (current?.kind === 'init' ? current : null));
    }, [updateHistory]);

    const canSend = isReady && !isSending && voiceStatus === 'idle' && input.trim().length > 0;
    const isVoiceAvailable = assistantVoiceCapture.isAvailable();
    const canRecord = isReady && voiceStatus === 'idle' && isVoiceAvailable;

    const sendMessage = useCallback(async () => {
        const text = input.trim();
        if (!text || isSending || !isReady) return;

        const nextHistory: ChatMessage[] = [...historyRef.current, { role: 'user', content: text }];
        updateHistory(nextHistory);
        setInput('');
        setError(null);
        setIsSending(true);

        const startedAt = Date.now();

        try {
            const res = await fetch(`${BASE_URL}/ai/v1/assistant/${projectId}/chat`, {
                method: 'POST',
                headers: authHeaders('application/json'),
                body: JSON.stringify({
                    messages: nextHistory.map(({ role, content }) => ({ role, content }))
                })
            });

            const data = (await res.json().catch(() => ({}))) as AssistantChatResponse;

            if (!res.ok || typeof data.answer !== 'string') {
                throw new Error(data.error || localization.getText('assistant_chat.request_failed'));
            }

            const sources = Array.isArray(data.sources)
                ? data.sources.map(source => source.title).filter((title): title is string => typeof title === 'string' && title.length > 0)
                : [];

            updateHistory([
                ...nextHistory,
                {
                    role: 'assistant',
                    content: data.answer,
                    timingMs: typeof data.timing?.totalMs === 'number' ? data.timing.totalMs : Date.now() - startedAt,
                    ...(sources.length > 0 ? { sources } : {})
                }
            ]);
        } catch (sendError: unknown) {
            // Roll back the optimistic user message and give the text back so nothing typed is lost.
            const current = historyRef.current;
            const last = current[current.length - 1];
            if (last?.role === 'user' && last.content === text) {
                updateHistory(current.slice(0, -1));
            }
            setInput(currentInput => (currentInput.trim() ? currentInput : text));
            setError({ kind: 'request', message: errorMessage(sendError, localization.getText('assistant_chat.network_error')) });
        } finally {
            setIsSending(false);
        }
    }, [authHeaders, input, isReady, isSending, localization, projectId, updateHistory]);

    const startRecording = useCallback(() => {
        if (!canRecord || voiceCaptureActiveRef.current) return;

        setError(current => (current?.kind === 'voice' ? null : current));

        try {
            assistantVoiceCapture.start();
            voiceCaptureActiveRef.current = true;
            setVoiceStatus('recording');
            setRecordingStartedAt(Date.now());
        } catch (recordingError: unknown) {
            setError({ kind: 'voice', message: errorMessage(recordingError, localization.getText('assistant_chat.recording_failed')) });
        }
    }, [canRecord, localization]);

    const finishRecording = useCallback(async () => {
        if (!voiceCaptureActiveRef.current) return;

        voiceCaptureActiveRef.current = false;
        setVoiceStatus('transcribing');
        setRecordingStartedAt(null);
        const requestId = ++voiceRequestIdRef.current;

        try {
            const audio = await assistantVoiceCapture.stop();
            if (voiceRequestIdRef.current !== requestId) return;

            const res = await fetch(`${BASE_URL}/ai/v1/transcribe`, {
                method: 'POST',
                headers: authHeaders('audio/ogg'),
                body: audio
            });
            const data = (await res.json().catch(() => ({}))) as TranscriptionResponse;

            if (voiceRequestIdRef.current !== requestId) return;
            if (!res.ok || typeof data.text !== 'string') {
                throw new Error(data.error || localization.getText('assistant_chat.transcription_failed'));
            }

            const transcript = data.text.trim();
            if (transcript) {
                setInput(current => (current.trim() ? `${current.trimEnd()} ${transcript}` : transcript));
            }
        } catch (transcriptionError: unknown) {
            if (voiceRequestIdRef.current !== requestId) return;
            setError({
                kind: 'voice',
                message: errorMessage(transcriptionError, localization.getText('assistant_chat.transcription_failed'))
            });
        } finally {
            if (voiceRequestIdRef.current === requestId) {
                setVoiceStatus('idle');
            }
        }
    }, [authHeaders, localization]);

    const cancelVoice = useCallback(() => {
        if (voiceStatus === 'idle') return;
        resetVoice();
    }, [resetVoice, voiceStatus]);

    return {
        history,
        input,
        setInput,
        isInitializing,
        isReady,
        isSending,
        error,
        canSend,
        sendMessage,
        clearConversation,
        retryInit,
        dismissError,
        voice: {
            status: voiceStatus,
            startedAt: recordingStartedAt,
            isAvailable: isVoiceAvailable,
            canRecord,
            start: startRecording,
            finish: finishRecording,
            cancel: cancelVoice
        }
    };
}

export type AssistantChatController = ReturnType<typeof useAssistantChat>;
